/** @jsxImportSource @emotion/react */
import { useSortable } from "@dnd-kit/sortable";
import { css } from "@emotion/react";
import styled from "@emotion/styled";
import { motion } from "framer-motion";
import React, { useMemo, useState } from "react";
import { AiOutlineMenu } from "react-icons/ai";
import { FaPencilAlt, FaRegTrashAlt } from "react-icons/fa";
import { useRecoilState } from "recoil";
import { LabelAtom } from "../../atoms/LabelAtom";
import { ILabel } from "../../types/LabelTypes";
import IconBerserkerMail from "../Icon";
import Warning from "../Modal/container/Warning";
import { useModal } from "../Modal/Modal";
import EditLabelModal from "./EditLabel";

const LabelCard = styled(motion.li)`
  width: 100%;

  display: flex;
  align-items: center;
  justify-content: center;

  padding: 8px;

  margin-top: 10px;

  background-color: #fff;
  border: 1px solid #eceeee;

  &:first-of-type {
    margin-top: 0px;
  }
`;

const Label = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;

  width: 15px;
  height: 15px;

  border-radius: 50%;
  border: 2px solid;
`;

export const Flex = styled.div`
  display: flex;
  align-items: center;
`;

const CardCont = styled.div`
  width: 100%;

  padding: 4px 8px;

  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const Name = styled.div`
  font-size: 16px;
  font-weight: 400;

  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;

  flex: 1;
`;

interface Props {
  label: ILabel;
  labelCss: any;
  id: string;
  index: number;
  deleteLabel: (id: string) => void;
}

const initialStyles = {
  x: 0,
  y: 0,
  scale: 1,
};

export default function EditLabelCard(props: Props): JSX.Element | null {
  const { label, labelCss, id, index, deleteLabel } = props;
  const [labels, setLabels] = useRecoilState(LabelAtom);
  const [iconColor, setIconColor] = useState<string>("#979797");
  const modal = useModal();

  const { attributes, setNodeRef, listeners, transform, isDragging } =
    useSortable({
      id,
      transition: null,
    });

  const Container = useMemo(() => {
    if (!labels) return null;
    return (
      <CardCont>
        <Flex
          css={css`
            overflow: hidden;
            grid-gap: 8px;
            > svg {
              color: #979797;
              cursor: grabbing;
            }
          `}
        >
          <AiOutlineMenu {...listeners} />

          <Label
            css={css`
              background-color: ${labelCss?.color || label.color};
              border-color: ${labelCss?.borderColor || label.color};
            `}
          />
          <Name>{label.name}</Name>
        </Flex>
        {true && (
          <Flex
            css={css`
              padding-left: 16px;
              > svg + svg {
                margin-left: 8px;
              }
              > svg {
                color: #979797;
                cursor: pointer;
                transition: color 0.3s;
                &:hover {
                  color: #fb972e;
                }
              }
            `}
          >
            <FaPencilAlt
              css={css`
                margin-right: 16px;
              `}
              onClick={() => {
                modal.pushModal(
                  <EditLabelModal
                    labelIndex={index}
                    labels={(labels || [])
                      .slice()
                      .sort((a, b) => a.labelIndex - b.labelIndex)}
                  />,
                  "Edit label"
                );
              }}
            />

            <FaRegTrashAlt
              onClick={() => {
                modal.pushModal(
                  <Warning
                    icon={"./icons/delete.svg"}
                    title="Delete Label"
                    description={`Are you sure you want to delete your label “${label.name}”?`}
                    buttonsName={["No", "Yes"]}
                    onOkClicked={() => {
                      deleteLabel(label._id as string);
                      modal.popModal();
                    }}
                    onCancelClicked={() => modal.popModal()}
                  />
                );
              }}
            />
          </Flex>
        )}
      </CardCont>
    );
  }, [labels, label, labelCss, id]);

  return (
    <LabelCard
      ref={setNodeRef}
      {...attributes}
      tabIndex={0}
      layoutId={label._id}
      animate={
        transform
          ? {
              x: transform.x,
              y: transform.y,
              scale: isDragging ? 1.05 : 1,
              zIndex: isDragging ? 1 : 0,
              boxShadow: isDragging
                ? "0 0 0 1px rgba(63, 63, 68, 0.05), 0px 15px 15px 0 rgba(34, 33, 81, 0.25)"
                : undefined,
            }
          : initialStyles
      }
      transition={{
        duration: !isDragging ? 0.25 : 0,
        easings: {
          type: "spring",
        },
        scale: {
          duration: 0.25,
        },
        zIndex: {
          delay: isDragging ? 0 : 0.25,
        },
      }}
    >
      {Container}
    </LabelCard>
  );
}
